import React, { useState, useEffect } from "react";
import "./landing.css";
import Layout from "../../../components/Layout/Layout";
import { CreateUserModal, ConfirmModal } from "../../../components";
import { MdOutlineCancelPresentation, MdOutlineLockReset } from "react-icons/md";
import { AiOutlineCheckSquare } from "react-icons/ai";
import { BiUserPin } from 'react-icons/bi';
import { FiEdit, FiFilter } from 'react-icons/fi';
import { FaUserMd, FaPlus } from 'react-icons/fa';

import Tippy from "@tippyjs/react";
import "tippy.js/dist/tippy.css";
import BeatLoader from "react-spinners/BeatLoader";

import { db } from "../../../firebase/firebase-config";
import { Table, Input, Select, Card, Tag, Button, Space, Tooltip } from 'antd';
import { collection, query, where, getDocs, doc, updateDoc } from "firebase/firestore";
import { getCurrentUserId } from "../../../utils/storage";
import DoctorDetailsModal from "../../../components/ui/DoctorDetailsModal/DoctorDetails";
import UpdateDoctorModal from "../../../components/ui/UpdateDoctor/UpdateDoctor";
import { getAuth, sendPasswordResetEmail } from "firebase/auth";
import { toast } from "react-toastify";

const { Search } = Input;
const { Option } = Select;

interface Doctor {
  id: string;
  username: string;
  email: string;
  status: boolean;
  speciality: string;
  [key: string]: any;
}

function Index(): JSX.Element {
  const [userData, setUserData] = useState<Doctor[]>([]);
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [showUpdateModal, setShowUpdateModal] = useState<boolean>(false);
  const [activeUpdateData, setActiveUpdateData] = useState<Doctor | null>(null);
  const [searchText, setSearchText] = useState<string>('');
  const [filteredData, setFilteredData] = useState<Doctor[]>([]);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterSpeciality, setFilterSpeciality] = useState<string>('all');

  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
  const [showConfirmActivateModal, setShowConfirmActivateModal] = useState<boolean>(false);
  const [currentActiveId, setCurrentActiveId] = useState<string | null>(null);

  const [showDoctorDetailsModal, setShowDoctorDetailsModal] = useState<boolean>(false);
  const [activeUserData, setActiveUserData] = useState<Doctor | null>(null);

  const [showConfirmResetPassword, setShowConfirmResetPassword] = useState<boolean>(false);

  const [loading, setLoading] = useState<boolean>(false);
  const [specialties, setSpecialties] = useState<string[]>([]);

  const toggleAddModal = (): void => {
    setShowCreateModal(!showCreateModal);
  };

  const toggleUpdateModal = (): void => {
    setShowUpdateModal(!showUpdateModal);
  };

  const handleContentAdded = (): void => {
    toggleAddModal();
    fetchData();
  };

  const resetFilters = (): void => {
    setSearchText('');
    setFilterStatus('all');
    setFilterSpeciality('all');
  };
  
  const rows: any[] = [
    {
      title: "#",
      dataIndex: "index",
      key: "index",
      width: 60,
      render: (text: any, record: any, index: number) => index + 1,
    },
    {
      title: "Display Name",
      dataIndex: "username",
      key: "username",
      sorter: (a: any, b: any) => a.username.localeCompare(b.username),
      render: (text: string, record: any) => (
        <div className="doctor-name">
          <span className="doctor-avatar">{text.charAt(0).toUpperCase()}</span>
          <span>{text}</span>
        </div>
      ),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      sorter: (a: any, b: any) => a.email.localeCompare(b.email),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      sorter: (a: any, b: any) => (a.status ? 1 : -1) - (b.status ? 1 : -1),
      render: (status: boolean) => (
        status ? 
        <Tag color="success" className="status-tag active">Active</Tag> : 
        <Tag color="error" className="status-tag inactive">Inactive</Tag>
      ),
    },
    {
      title: "Speciality",
      dataIndex: "speciality",
      key: "speciality",
      sorter: (a: any, b: any) => a.speciality.localeCompare(b.speciality),
      render: (speciality: string) => {
        let displayName = speciality;
        if (speciality === "pt") displayName = "Pathologist";
        else if (speciality === "lab") displayName = "Lab Technician";
        
        return (
          <Tag color="purple" className="speciality-tag">
            {displayName}
          </Tag>
        );
      },
    },
    {
      title: "Action",
      dataIndex: "action",
      key: "action",
      width: 240,
      render: (text: any, record: Doctor) => (
        <Space size="small">
          <Tooltip title="Edit Doctor">
            <Button 
              type="primary" 
              shape="circle" 
              icon={<FiEdit size={16} />} 
              onClick={() => {
                toggleUpdateModal();
                setActiveUpdateData(record);
              }}
            />
          </Tooltip>
          
          <Tooltip title="Doctor Details">
            <Button 
              type="default" 
              shape="circle" 
              icon={<BiUserPin size={16} />} 
              onClick={() => {
                setActiveUserData(record);
                setShowDoctorDetailsModal(true);
              }}
            />
          </Tooltip>
          
          <Tooltip title="Reset Password">
            <Button 
              style={{ background: '#4F5052', color: 'white' }} 
              shape="circle" 
              icon={<MdOutlineLockReset size={16} />} 
              onClick={() => {
                setActiveUserData(record);
                setShowConfirmResetPassword(true);
              }}
            />
          </Tooltip>
          
          {record.status === false && (
            <Tooltip title="Activate Account">
              <Button 
                type="primary" 
                shape="circle" 
                className="activate-btn"
                style={{ background: '#10b981', borderColor: '#10b981' }}
                icon={<AiOutlineCheckSquare size={16} />} 
                onClick={() => {
                  setCurrentActiveId(record.id);
                  setShowConfirmActivateModal(true);
                }}
              />
            </Tooltip>
          )}
          
          {record.status === true && (
            <Tooltip title="Suspend Account">
              <Button 
                danger 
                shape="circle" 
                icon={<MdOutlineCancelPresentation size={16} />} 
                onClick={() => {
                  setCurrentActiveId(record.id);
                  setShowConfirmModal(true);
                }}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];
  
  const fetchData = async (): Promise<void> => {
    setLoading(true);

    try {
      const users = await getDocs(collection(db, "users"));
      
      const res: Doctor[] = [];
      const uniqueSpecialties = new Set<string>();
      
      users.forEach((user) => {
        const userData = {
          id: user.id,
          ...user.data(),
        } as Doctor;
        res.push(userData);
        
        // Collect unique specialties for filtering
        if (userData.speciality) {
          uniqueSpecialties.add(userData.speciality);
        }
      });
      
      setUserData(res);
      setSpecialties(Array.from(uniqueSpecialties));
    } catch (error: any) {
      console.error("Error fetching data:", error);
      toast.error("Failed to fetch doctors data");
    } finally {
      setLoading(false);
    }
  };

  async function suspendAccount(doctorId: string | null): Promise<void> {
    try {
      if (doctorId) {
        const docRef = doc(collection(db, "users"), doctorId);
        await updateDoc(docRef, { status: false });
        fetchData();
        setShowConfirmModal(false);
        toast.success("Doctor account suspended successfully");
      }
    } catch (error: any) {
      console.error("Error suspending account:", error);
      toast.error("Failed to suspend doctor account");
    }
  }

  async function activateAccount(doctorId: string | null): Promise<void> {
    try {
      if (doctorId) {
        const docRef = doc(collection(db, "users"), doctorId);
        await updateDoc(docRef, { status: true });
        fetchData();
        setShowConfirmActivateModal(false);
        toast.success("Doctor account activated successfully");
      }
    } catch (error: any) {
      console.error("Error activating account:", error);
      toast.error("Failed to activate doctor account");
    }
  }

  const resetPassword = (): void => {
    try {
      const auth = getAuth();
      if (activeUserData?.email) {
        sendPasswordResetEmail(auth, activeUserData.email)
          .then(() => {
            toast.success('Password reset email sent');
            setShowConfirmResetPassword(false);
          })
          .catch((error: any) => {
            console.error("Error sending password reset email:", error);
            toast.error("Error sending password reset email");
            setShowConfirmResetPassword(false);
          });
      }
    } catch (error: any) {
      console.error("Error in reset password function:", error);
      toast.error("An error occurred");
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    // Apply both search and filters
    let filtered = userData;
    
    // Apply search
    if (searchText) {
      filtered = filtered.filter((item: Doctor) =>
        Object.values(item).some((value: any) =>
          value?.toString().toLowerCase().includes(searchText.toLowerCase())
        )
      );
    }
    
    // Apply status filter
    if (filterStatus !== 'all') {
      const statusValue = filterStatus === 'active';
      filtered = filtered.filter((item: Doctor) => item.status === statusValue);
    }
    
    // Apply speciality filter
    if (filterSpeciality !== 'all') {
      filtered = filtered.filter((item: Doctor) => item.speciality === filterSpeciality);
    }
    
    setFilteredData(filtered);
  }, [userData, searchText, filterStatus, filterSpeciality]);

  const handleSearch = (value: string): void => {
    setSearchText(value);
  };

  const getSpecialityDisplay = (code: string): string => {
    if (code === "pt") return "Pathologist";
    if (code === "lab") return "Lab Technician";
    return code;
  };

  const getStatusCount = (status: string): number => {
    if (status === 'active') {
      return userData.filter((user: Doctor) => user.status === true).length;
    }
    if (status === 'inactive') {
      return userData.filter((user: Doctor) => user.status === false).length;
    }
    return userData.length;
  };

  return (
    <>
      <Layout title="Doctors Management" pageTitle="Doctors">
        <div className="section">
          <div className="doctors-dashboard">
            {/* Summary Cards */}
            <div className="summary-cards">
              <Card className="summary-card total">
                <div className="card-icon">
                  <FaUserMd size={24} />
                </div>
                <div className="card-content">
                  <h3>{userData.length}</h3>
                  <p>Total Doctors</p>
                </div>
              </Card>
              
              <Card className="summary-card active">
                <div className="card-icon">
                  <AiOutlineCheckSquare size={24} />
                </div>
                <div className="card-content">
                  <h3>{getStatusCount('active')}</h3>
                  <p>Active Doctors</p>
                </div>
              </Card>
              
              <Card className="summary-card inactive">
                <div className="card-icon">
                  <MdOutlineCancelPresentation size={24} />
                </div>
                <div className="card-content">
                  <h3>{getStatusCount('inactive')}</h3>
                  <p>Inactive Doctors</p>
                </div>
              </Card>
            </div>
            
            <div className="parent-con">
              <div className="data-table">
                <div className="top">
                  <div className="span">
                    <h3>Doctors Directory</h3>
                  </div>

                  <Button
                    type="primary"
                    icon={<FaPlus />}
                    onClick={toggleAddModal}
                    className="add-doctor-btn"
                  >
                    Add Doctor
                  </Button>
                </div>
                
                <div className="filter-section">
                  <Search
                    placeholder="Search doctors..."
                    allowClear
                    onSearch={handleSearch}
                    onChange={(e) => handleSearch(e.target.value)}
                    value={searchText}
                    style={{ width: 250 }}
                  />
                  
                  <Space>
                    <Select
                      value={filterStatus}
                      onChange={setFilterStatus}
                      className="select-field"
                      style={{ width: 150 }}
                    >
                      <Option value="all">All Status</Option>
                      <Option value="active">Active</Option>
                      <Option value="inactive">Inactive</Option>
                    </Select>
                    
                    <Select
                      value={filterSpeciality}
                      onChange={setFilterSpeciality}
                      className="select-field"
                      style={{ width: 180 }}
                    >
                      <Option value="all">All Specialities</Option>
                      {specialties.map(spec => (
                        <Option key={spec} value={spec}>
                          {getSpecialityDisplay(spec)}
                        </Option>
                      ))}
                    </Select>
                    
                    <Button 
                      icon={<FiFilter />} 
                      onClick={resetFilters}
                    >
                      Reset
                    </Button>
                  </Space>
                </div>
                
                <div className="table-container">
                  {loading ? (
                    <div className="loading-container">
                      <BeatLoader color="#322659" />
                    </div>
                  ) : (
                    <Table
                      dataSource={filteredData.map((item: Doctor, index: number) => ({ ...item, key: item.id, index: index + 1 }))}
                      columns={rows}
                      rowKey="id"
                      showSorterTooltip={false}
                      pagination={{ 
                        pageSize: 10,
                        showSizeChanger: true,
                        pageSizeOptions: ['10', '20', '50'],
                        showTotal: (total: number) => `Total ${total} doctors`
                      }}
                      className="doctors-table"
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </Layout>

      {showConfirmModal && (
        <ConfirmModal 
          title="Are you sure you want to suspend this doctor's account?"
          onAccept={() => suspendAccount(currentActiveId)}
          onCancel={() => setShowConfirmModal(false)}
        />
      )}

      {showConfirmActivateModal && (
        <ConfirmModal 
          title="Are you sure you want to activate this doctor's account?"
          onAccept={() => activateAccount(currentActiveId)}
          onCancel={() => setShowConfirmActivateModal(false)}
        />
      )}

      {showConfirmResetPassword && (
        <ConfirmModal 
          title="Are you sure you want to reset this doctor's password?"
          onAccept={resetPassword}
          onCancel={() => setShowConfirmResetPassword(false)}
        />
      )}

      {showCreateModal && (
        <CreateUserModal
          onContentAdded={handleContentAdded}
          onClose={toggleAddModal}
        />
      )}

      {showUpdateModal && (
        <UpdateDoctorModal
          activeData={activeUpdateData}
          onContentAdded={handleContentAdded}
          onClose={toggleUpdateModal}
        />
      )}

      {showDoctorDetailsModal && (
        <DoctorDetailsModal 
          user={activeUserData} 
          onCancel={() => setShowDoctorDetailsModal(false)} 
        />
      )}
    </>
  );
}

export default Index;